import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  Button,
  Space,
  Tag,
  Tooltip,
  Select,
  Modal,
  Popconfirm,
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  EyeOutlined,
  DeleteOutlined,
  FileTextOutlined,
  InboxOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CarOutlined,
  LockOutlined,
} from '@ant-design/icons';
import { hasPermission } from '../../utils/permissions';
import {
  fetchDeliveryChallansRequest,
  deleteDeliveryChallanRequest,
  fetchDeliveryStatisticsRequest,
} from '../../redux/actions/deliveryChallanActions';
import DeliveryChallanForm from './DeliveryChallanForm';
import DeliveryChallanDetail from './DeliveryChallanDetail';
import './DeliveryChallan.css';

const { Option } = Select;

const DeliveryChallan = () => {
  const dispatch = useDispatch();
  const { challans, pagination, loading, statistics } = useSelector(
    (state) => state.deliveryChallans
  );
  const permissions = useSelector((state) => state.auth.permissions || []);

  const [isFormVisible, setIsFormVisible] = useState(false);
  const [isDetailVisible, setIsDetailVisible] = useState(false);
  const [selectedChallan, setSelectedChallan] = useState(null);
  const [filters, setFilters] = useState({
    page: 1,
    limit: 10,
    delivery_status: '',
  });

  // Permissions
  const canView = hasPermission(permissions, 'delivery_challan/view');
  const canCreate = hasPermission(permissions, 'delivery_challan/create');
  const canDelete = hasPermission(permissions, 'delivery_challan/delete');

  useEffect(() => {
    if (canView) {
      const params = { ...filters };
      if (!params.delivery_status) delete params.delivery_status;
      dispatch(fetchDeliveryChallansRequest(params));
      dispatch(fetchDeliveryStatisticsRequest());
    }
  }, [dispatch, filters, canView]);

  const handleRefresh = () => {
    const params = { ...filters };
    if (!params.delivery_status) delete params.delivery_status;
    dispatch(fetchDeliveryChallansRequest(params));
    dispatch(fetchDeliveryStatisticsRequest());
  };

  const handleView = (challan) => {
    setSelectedChallan(challan);
    setIsDetailVisible(true);
  };

  const handleDelete = (id) => {
    dispatch(deleteDeliveryChallanRequest(id));
  };

  const handlePageChange = (page, pageSize) => {
    setFilters((prev) => ({ ...prev, page, limit: pageSize }));
  };

  const handleStatusFilter = (value) => {
    setFilters((prev) => ({ ...prev, delivery_status: value || '', page: 1 }));
  };

  const getStatusTag = (status) => {
    const map = {
      pending: { color: 'orange', text: 'Pending' },
      in_transit: { color: 'blue', text: 'In Transit' },
      partially_delivered: { color: 'purple', text: 'Partially Delivered' },
      delivered: { color: 'green', text: 'Delivered' },
      cancelled: { color: 'red', text: 'Cancelled' },
      received: { color: 'cyan', text: 'Received' },
      verified: { color: 'green', text: 'Verified' },
      rejected: { color: 'red', text: 'Rejected' },
      partial: { color: 'orange', text: 'Partial' },
    };
    const config = map[status] || { color: 'default', text: status || '-' };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  // Real statistics from GET /api/delivery/statistics
  const totalChallans = statistics?.total_challans ?? pagination?.total ?? challans.length;
  const pendingDeliveries = statistics?.pending_deliveries ?? 0;
  const recentDeliveries = statistics?.recent_deliveries ?? 0;
  const deliveredCount =
    statistics?.orders_by_status?.find((s) => s.delivery_status === 'delivered')?.count ?? 0;

  const columns = [
    {
      title: 'Challan #',
      dataIndex: 'challan_number',
      key: 'challan_number',
      width: 140,
      fixed: 'left',
      render: (text, record) => (
        <div>
          <strong style={{ color: '#1890ff' }}>{text}</strong>
          {record.is_partial_delivery && (
            <Tag color="orange" style={{ marginLeft: 4, fontSize: 10 }}>Partial</Tag>
          )}
        </div>
      ),
    },
    {
      title: 'PO / Vendor',
      key: 'po_vendor',
      width: 160,
      ellipsis: true,
      render: (_, record) => {
        const vendor = record.purchase_order?.vendor_name || record.vendor_name || '—';
        return (
          <div style={{ lineHeight: 1.4 }}>
            <div style={{ fontWeight: 600, fontSize: 12 }}>#{record.purchase_order_id}</div>
            <Tooltip title={vendor}>
              <div style={{ color: '#595959', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{vendor}</div>
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: 'Challan Date',
      dataIndex: 'challan_date',
      key: 'challan_date',
      width: 100,
      render: (date) =>
        date ? new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—',
    },
    {
      title: 'Delivery Date',
      dataIndex: 'delivery_date',
      key: 'delivery_date',
      width: 100,
      render: (date) =>
        date ? new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—',
    },
    {
      title: 'Status',
      dataIndex: 'delivery_status',
      key: 'delivery_status',
      width: 130,
      render: (status) => getStatusTag(status),
    },
    {
      title: 'Received By',
      dataIndex: 'received_by_name',
      key: 'received_by_name',
      width: 120,
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text}>
          <span style={{ fontSize: 12 }}>{text || '—'}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 90,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              type="primary"
              ghost
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleView(record)}
            />
          </Tooltip>
          {canDelete && (
            <Popconfirm
              title="Delete this delivery challan?"
              description="This will revert delivery quantities on the PO."
              onConfirm={() => handleDelete(record.id)}
              okText="Delete"
              cancelText="Cancel"
              okType="danger"
            >
              <Tooltip title="Delete">
                <Button danger icon={<DeleteOutlined />} size="small" />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  if (!canView) {
    return (
      <div className="delivery-challan-container">
        <div className="empty-state">
          <div className="empty-state-icon"><LockOutlined /></div>
          <div className="empty-state-text">
            You don't have permission to view delivery challans.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-content">
        {/* Page Header */}
        <div className="page-header">
          <div className="page-header-title">
            <div className="page-header-icon"><InboxOutlined /></div>
            <div className="page-header-text">
              <h1>Delivery Challans</h1>
              <p>Track and manage delivery challans for purchase orders</p>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#e6f4ff', color: '#1890ff' }}><FileTextOutlined /></div>
            <div className="stat-content">
              <div className="stat-value" style={{ color: '#1890ff' }}>{totalChallans}</div>
              <div className="stat-label">Total Challans</div>
            </div>
          </div>
          <div className="stat-card" style={{ borderLeftColor: '#52c41a' }}>
            <div className="stat-icon" style={{ background: '#f6ffed', color: '#52c41a' }}><CheckCircleOutlined /></div>
            <div className="stat-content">
              <div className="stat-value" style={{ color: '#52c41a' }}>{deliveredCount}</div>
              <div className="stat-label">Fully Delivered</div>
            </div>
          </div>
          <div className="stat-card" style={{ borderLeftColor: '#fa8c16' }}>
            <div className="stat-icon" style={{ background: '#fff7e6', color: '#fa8c16' }}><ClockCircleOutlined /></div>
            <div className="stat-content">
              <div className="stat-value" style={{ color: '#fa8c16' }}>{pendingDeliveries}</div>
              <div className="stat-label">Pending</div>
            </div>
          </div>
          <div className="stat-card" style={{ borderLeftColor: '#722ed1' }}>
            <div className="stat-icon" style={{ background: '#f9f0ff', color: '#722ed1' }}><CarOutlined /></div>
            <div className="stat-content">
              <div className="stat-value" style={{ color: '#722ed1' }}>{recentDeliveries}</div>
              <div className="stat-label">Last 7 days</div>
            </div>
          </div>
        </div>

        {/* Delivery Status Distribution — compact inline strip */}
        {statistics?.orders_by_status?.length > 0 && (
          <div style={{ background: '#fff', borderRadius: 8, padding: '8px 14px', marginBottom: 14, boxShadow: '0 1px 4px rgba(0,0,0,.05)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#666', marginRight: 4 }}>PO Status:</span>
            {statistics.orders_by_status.map((item) => (
              <span key={item.delivery_status} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                {getStatusTag(item.delivery_status)}
                <span style={{ fontSize: 12, fontWeight: 600, color: '#333' }}>{item.count}</span>
              </span>
            ))}
          </div>
        )}

        {/* Table */}
        <div className="modern-table-container">
          <div className="table-header">
            <h2 className="table-title">All Delivery Challans</h2>
            <Space>
              <Select
                placeholder="Filter by status"
                style={{ width: 190 }}
                allowClear
                value={filters.delivery_status || undefined}
                onChange={handleStatusFilter}
              >
                <Option value="pending">Pending</Option>
                <Option value="in_transit">In Transit</Option>
                <Option value="partially_delivered">Partially Delivered</Option>
                <Option value="delivered">Delivered</Option>
                <Option value="cancelled">Cancelled</Option>
              </Select>
              <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
                Refresh
              </Button>
              {canCreate && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setIsFormVisible(true)}
                >
                  Record New Delivery
                </Button>
              )}
            </Space>
          </div>

          <Table
            columns={columns}
            dataSource={challans}
            rowKey="id"
            loading={loading}
            pagination={{
              current: filters.page,
              pageSize: filters.limit,
              total: pagination?.total || 0,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} challans`,
              onChange: handlePageChange,
            }}
            scroll={{ x: 'max-content' }}
            size="small"
            locale={{
              emptyText: (
                <div className="empty-state">
                  <div className="empty-state-icon"><InboxOutlined /></div>
                  <div className="empty-state-text">
                    No delivery challans found
                  </div>
                  {canCreate && (
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => setIsFormVisible(true)}
                    >
                      Record First Delivery
                    </Button>
                  )}
                </div>
              ),
            }}
          />
        </div>
      </div>

      {/* Create / Edit Modal */}
      <Modal
        title={
          <Space>
            <FileTextOutlined />
            Record Delivery Challan
          </Space>
        }
        open={isFormVisible}
        onCancel={() => setIsFormVisible(false)}
        footer={null}
        width={1000}
        destroyOnClose
      >
        <DeliveryChallanForm
          onSuccess={() => {
            setIsFormVisible(false);
            handleRefresh();
          }}
        />
      </Modal>

      {/* Detail Modal */}
      <Modal
        title={
          <Space>
            <EyeOutlined />
            Delivery Challan Details
          </Space>
        }
        open={isDetailVisible}
        onCancel={() => setIsDetailVisible(false)}
        footer={null}
        width={900}
        destroyOnClose
      >
        <DeliveryChallanDetail
          challan={selectedChallan}
          onClose={() => {
            setIsDetailVisible(false);
            handleRefresh();
          }}
        />
      </Modal>
    </div>
  );
};

export default DeliveryChallan;
